import React from 'react';
import { SkeletonBlock, SkeletonRegion, TableRowsSkeleton } from './BrandPresenceSkeleton';
import { BP_CARD_CLASSES } from './brandPresenceClasses';

/** Bar heights of the chart shape, as fractions of its height, so it reads as data rather than a block. */
const CHART_BAR_HEIGHTS = ['h-[35%]', 'h-[55%]', 'h-[45%]', 'h-[70%]', 'h-[60%]', 'h-[80%]', 'h-[65%]', 'h-[90%]', 'h-[75%]', 'h-[85%]'];

function SectionSkeleton({ chart }: { chart: boolean }) {
  return (
    <div className={BP_CARD_CLASSES}>
      <div className="flex items-center justify-between border-b border-slate-200/70 px-5 py-3.5">
        <SkeletonBlock className="h-4 w-32" />
        <SkeletonBlock className="h-8 w-40 rounded-xl" />
      </div>
      <div className="p-5">
        {chart ? (
          <div className="flex h-56 items-end gap-3">
            {CHART_BAR_HEIGHTS.map((height, index) => (
              <SkeletonBlock key={index} className={`w-full rounded-t-md rounded-b-none ${height}`} />
            ))}
          </div>
        ) : (
          <TableRowsSkeleton />
        )}
      </div>
    </div>
  );
}

/** A card of ranked rows, each a name and a figure over a meter, as Gaps and Quality issues show them. */
function MeterListSectionSkeleton() {
  return (
    <div className={BP_CARD_CLASSES}>
      <div className="flex min-h-[4.3125rem] items-center justify-between border-b border-slate-200/70 px-5 py-3.5">
        <SkeletonBlock className="h-4 w-28" />
      </div>
      <div className="divide-y divide-slate-100 px-5 py-2.5">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((row) => (
          <div key={row} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 py-2.5">
            <SkeletonBlock className={`h-4 ${row % 2 ? 'w-1/3' : 'w-1/2'}`} />
            <SkeletonBlock className="h-4 w-10" />
            <SkeletonBlock className="col-span-2 h-1.5 w-full rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** A card holding a grid of cells: a column of names, then a row of cells per name. */
function GridSectionSkeleton() {
  return (
    <div className={BP_CARD_CLASSES}>
      <div className="border-b border-slate-200/70 px-5 py-3.5">
        <SkeletonBlock className="h-4 w-32" />
        <SkeletonBlock className="mt-2 h-3 w-80 max-w-full" />
      </div>
      <div className="space-y-2 p-5">
        {[0, 1, 2, 3, 4].map((row) => (
          <div key={row} className="grid grid-cols-[12rem_repeat(6,minmax(0,1fr))] gap-2">
            <SkeletonBlock className="h-11 w-full" />
            {[0, 1, 2, 3, 4, 5].map((cell) => (
              <SkeletonBlock key={cell} className="h-11 w-full rounded-lg" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * An analytics screen while its first answer is on the way: the header, the tabs and filters, a row
 * of figure cards, a chart and a table — each where the real one lands.
 */
export function AnalyticsScreenSkeleton({
  label,
  kpiCount = 4,
  withTabs = true,
  layout = 'chart-and-table',
}: {
  label: string;
  kpiCount?: number;
  withTabs?: boolean;
  /**
   * The cards under the figures: a chart and a table, or Content Health's chart, two ranked lists
   * side by side, and two grids.
   */
  layout?: 'chart-and-table' | 'content-health';
}) {
  return (
    <SkeletonRegion label={label} className="w-full h-full bg-slate-50">
      <div className="max-w-[1800px] mx-auto px-6 py-8">
        <div className="mb-8">
          <SkeletonBlock className="h-3 w-44" />
          <SkeletonBlock className="mt-3 h-8 w-64 rounded-lg" />
          <SkeletonBlock className="mt-3 h-4 w-[30rem] max-w-full" />
        </div>
        {withTabs ? (
          <div className="mb-4 flex gap-6 border-b border-slate-200/70 pb-3">
            <SkeletonBlock className="h-4 w-24" />
            <SkeletonBlock className="h-4 w-28" />
            <SkeletonBlock className="h-4 w-20" />
          </div>
        ) : null}
        <div className={`mb-6 flex flex-wrap items-center gap-3 px-4 py-3 ${BP_CARD_CLASSES}`}>
          <SkeletonBlock className="h-9 w-44 rounded-lg" />
          <SkeletonBlock className="h-9 w-44 rounded-lg" />
          <SkeletonBlock className="h-9 w-52 rounded-lg" />
          <SkeletonBlock className="h-8 w-28 rounded-full" />
        </div>
        <div className="mb-6 flex flex-wrap gap-4">
          {Array.from({ length: kpiCount }, (_unused, card) => (
            <div key={card} className={`grow basis-[200px] min-w-[200px] p-5 ${BP_CARD_CLASSES}`}>
              <SkeletonBlock className="h-3 w-28" />
              <SkeletonBlock className="mt-4 h-9 w-20 rounded-lg" />
            </div>
          ))}
        </div>
        {layout === 'content-health' ? (
          <div className="space-y-6">
            <SectionSkeleton chart />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <MeterListSectionSkeleton />
              <MeterListSectionSkeleton />
            </div>
            <GridSectionSkeleton />
            <GridSectionSkeleton />
          </div>
        ) : (
          <div className="space-y-6">
            <SectionSkeleton chart />
            <SectionSkeleton chart={false} />
          </div>
        )}
      </div>
    </SkeletonRegion>
  );
}
